/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Plus, Search, MoreHorizontal, Edit, Trash2, ChevronLeft, ChevronRight, Settings, Key, User, Calendar } from "lucide-react"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import apiService from "@/services/apiService"
import { toast } from "sonner"
import { useAppStore } from "@/store/appStore"
import bgImage from "@/assets/favicon.png"
import { getUserInfo } from "@/services/userService"

interface SystemConfig {
  id: string
  key: string
  value: string
  createdBy: {
    id: string
    name: string
    email: string
  }
  createdById: string
  createdAt: string
  updatedAt: string
}

interface ConfigFormData {
  key: string
  value: string
}

export default function SystemConfigPage() {
  const { _systemConfigs } = useAppStore()
  const [configs, setConfigs] = useState<SystemConfig[]>(_systemConfigs || [])
  const [searchTerm, setSearchTerm] = useState("")
  const [showConfigForm, setShowConfigForm] = useState(false)
  const [editingConfig, setEditingConfig] = useState<SystemConfig | null>(null)
  const [deleteConfig, setDeleteConfig] = useState<SystemConfig | null>(null)
  const [formData, setFormData] = useState<ConfigFormData>({ key: "", value: "" })
  const [formErrors, setFormErrors] = useState<Partial<ConfigFormData>>({})

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const filteredConfigs = configs.filter(
    (config) =>
      config.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      config.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
      config.createdBy.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Pagination calculations
  const totalPages = Math.ceil(filteredConfigs.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentConfigs = filteredConfigs.slice(startIndex, endIndex)

  // Reset to first page when search changes
  const handleSearch = (value: string) => {
    setSearchTerm(value)
    setCurrentPage(1)
  }

  const handleCreateConfig = () => {
    setEditingConfig(null)
    setFormData({ key: "", value: "" })
    setFormErrors({})
    setShowConfigForm(true)
  }

  const handleEditConfig = (config: SystemConfig) => {
    setEditingConfig(config)
    setFormData({ key: config.key, value: config.value })
    setFormErrors({})
    setShowConfigForm(true)
  }

  const handleDeleteConfig = (config: SystemConfig) => {
    setDeleteConfig(config)
  }

  const validateForm = (): boolean => {
    const errors: Partial<ConfigFormData> = {}

    if (!formData.key.trim()) {
      errors.key = "Configuration key is required"
    } else if (!/^[a-zA-Z0-9._-]+$/.test(formData.key)) {
      errors.key = "Key can only contain letters, numbers, dots, underscores, and hyphens"
    } else if (!editingConfig && configs.some(c => c.key === formData.key)) {
      errors.key = "Configuration key already exists"
    }

    if (!formData.value.trim()) {
      errors.value = "Configuration value is required"
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSaveConfig = async () => {
    if (!validateForm()) return

    if (editingConfig) {
      // Update existing config
      const updatedConfig: SystemConfig = {
        ...editingConfig,
        key: formData.key,
        value: formData.value,
        updatedAt: new Date().toISOString()
      }
      const userInfo:any = getUserInfo(); 
      if (userInfo) {
        updatedConfig.createdById = userInfo.id
      }
      const systemConfig = await apiService.put<SystemConfig>(`/system-configs/${editingConfig.id}`, updatedConfig);
      if(systemConfig){
        setConfigs(configs.map(config => 
            config.id === editingConfig.id ? updatedConfig : config
        ))
        toast.success("Configuration updated successfully")
      }else{
        console.error("Failed to update system configuration")
      }
    } else {
      // Create new config
      const newConfig: SystemConfig = {
        id: Date.now().toString(),
        key: formData.key,
        value: formData.value,
        createdBy: {
          id: "1",
          name: "Inspector Sharma",
          email: "sharma@police.gov.in"
        },
        createdById: "1",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
      const userInfo:any = getUserInfo(); 
      if (userInfo) {
        newConfig.createdById = userInfo.id
      }
      const systemConfig = await apiService.post<SystemConfig>('/system-configs', newConfig);
      if(systemConfig){
        setConfigs([...configs, newConfig])
        toast.success("Configuration created successfully")
      }else{
        console.error("Failed to create system configuration")
      }       
    }

    setShowConfigForm(false)
    setEditingConfig(null)
    setFormData({ key: "", value: "" })
    setFormErrors({})
  }

  const confirmDelete = async () => {
    if (deleteConfig) {
      const systemConfig = await apiService.delete<SystemConfig>(`/system-configs/${deleteConfig.id}`);
      if(!systemConfig){
          console.error("Failed to delete system configuration")
          return
      }
      setConfigs(configs.filter(config => config.id !== deleteConfig.id))
      setDeleteConfig(null)
      // Adjust current page if necessary
      const newTotalPages = Math.ceil((filteredConfigs.length - 1) / itemsPerPage)
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages)
      }
      toast.success("Configuration deleted successfully")
    }
  }

  const getConfigTypeFromKey = (key: string) => {
    if (key.startsWith('auth.')) return 'Authentication'
    if (key.startsWith('system.')) return 'System'
    if (key.startsWith('security.')) return 'Security'
    if (key.startsWith('notification.')) return 'Notification'
    if (key.startsWith('api.')) return 'API'
    return 'General'
  }

  const getConfigTypeBadge = (key: string) => {
    const type = getConfigTypeFromKey(key)
    const colors = {
      'Authentication': 'bg-blue-100 text-blue-800',
      'System': 'bg-green-100 text-green-800',
      'Security': 'bg-red-100 text-red-800',
      'Notification': 'bg-yellow-100 text-yellow-800',
      'API': 'bg-purple-100 text-purple-800',
      'General': 'bg-gray-100 text-gray-800'
    }
    
    return (
      <Badge className={`${colors[type as keyof typeof colors]} hover:${colors[type as keyof typeof colors]}`}>
        {type}
      </Badge>
    )
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(Number.parseInt(value))
    setCurrentPage(1)
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString)?.toLocaleString() ?? "-"
  }

  const getConfigStats = () => {
    const types = configs.reduce((acc, config) => {
      const type = getConfigTypeFromKey(config.key)
      acc[type] = (acc[type] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    return {
      total: configs.length,
      types: Object.keys(types).length,
      mostCommonType: Object.entries(types).sort((a, b) => b[1] - a[1])[0]
    }
  }

  const stats = getConfigStats()

  const fetchSystemConfigs = async () => {
    // const systemConfigs = await apiService.get<SystemConfig[]>('/system-configs')
    if(_systemConfigs) {
        setConfigs(_systemConfigs)
    }else {
        console.error("Failed to fetch system configurations")
    }
  }

  useEffect(() => {
    const init = async () => {
        fetchSystemConfigs()
    }
    init();
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-r from-[#0d2846]/3 via-[#e2af58]/10 to-[#0d2846]/3 p-6">
      <div
        className="absolute inset-0 bg-no-repeat bg-center bg-contain opacity-3 pointer-events-none"
        style={{
          backgroundImage: `url(${bgImage})`,
        }}
      />
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-black">System Configuration</h1>
            <p className="text-gray-600 mt-1">Manage system settings and configuration parameters</p>
          </div>
          <Button onClick={handleCreateConfig} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
            <Plus className="h-4 w-4 mr-2" />
            Add Configuration
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center">
                <Settings className="h-8 w-8 text-blue-600 mr-3" />
                <div>
                  <div className="text-2xl font-bold text-black">{stats.total}</div>
                  <p className="text-sm text-gray-600">Total Configurations</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center">
                <Key className="h-8 w-8 text-green-600 mr-3" />
                <div>
                  <div className="text-2xl font-bold text-green-600">{stats.types}</div>
                  <p className="text-sm text-gray-600">Configuration Types</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center">
                <User className="h-8 w-8 text-purple-600 mr-3" />
                <div>
                  <div className="text-2xl font-bold text-purple-600">
                    {new Set(configs.map(c => c.createdById)).size}
                  </div>
                  <p className="text-sm text-gray-600">Contributors</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center">
                <Calendar className="h-8 w-8 text-orange-600 mr-3" />
                <div>
                  <div className="text-2xl font-bold text-orange-600">
                    {stats.mostCommonType?.[0] || 'N/A'}
                  </div>
                  <p className="text-sm text-gray-600">Most Common Type</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filters */}
        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg text-black">Configuration List</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-6">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search configurations..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10 border-gray-300"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Show:</span>
                <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-gray-600">per page</span>
              </div>
            </div>

            {/* Configuration Table */}
            <div className="border border-gray-200 rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-black font-semibold">Key</TableHead>
                    <TableHead className="text-black font-semibold">Value</TableHead>
                    <TableHead className="text-black font-semibold">Type</TableHead>
                    <TableHead className="text-black font-semibold">Created By</TableHead>
                    <TableHead className="text-black font-semibold">Created At</TableHead>
                    <TableHead className="text-black font-semibold">Updated At</TableHead>
                    <TableHead className="text-black font-semibold w-[50px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentConfigs.map((config) => (
                    <TableRow key={config.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium text-black max-w-xs">
                        <div className="truncate" title={config.key}>
                          {config.key}
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-600 max-w-xs">
                        <div className="truncate" title={config.value}>
                          {config.value}
                        </div>
                      </TableCell>
                      <TableCell>{getConfigTypeBadge(config.key)}</TableCell>
                      <TableCell className="text-gray-600">{config.createdBy.name}</TableCell>
                      <TableCell className="text-gray-600 text-sm">
                        {formatDateTime(config.createdAt)}
                      </TableCell>
                      <TableCell className="text-gray-600 text-sm">
                        {formatDateTime(config.updatedAt)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-gray-100">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white border border-gray-200">
                            <DropdownMenuItem
                              onClick={() => handleEditConfig(config)}
                              className="hover:bg-gray-50 cursor-pointer"
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDeleteConfig(config)}
                              className="hover:bg-red-50 text-red-600 cursor-pointer"
                            //   disabled={true}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {currentConfigs.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No configurations found matching your search criteria.
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-gray-600">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredConfigs.length)} of {filteredConfigs.length} configurations
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="border-gray-300"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter((page) => {
                        return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1
                      })
                      .map((page, index, array) => (
                        <div key={page} className="flex items-center">
                          {index > 0 && array[index - 1] !== page - 1 && (
                            <span className="px-2 text-gray-400">...</span>
                          )}
                          <Button
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(page)}
                            className={currentPage === page ? "bg-black text-white" : "border-gray-300"}
                          >
                            {page}
                          </Button>
                        </div>
                      ))}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="border-gray-300"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Configuration Form Dialog */}
      <Dialog open={showConfigForm} onOpenChange={setShowConfigForm}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-black">
              {editingConfig ? "Edit Configuration" : "Add Configuration"}
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              {editingConfig 
                ? "Update the configuration key and value." 
                : "Create a new system configuration setting."
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="config-key" className="text-black">Configuration Key</Label>
              <Input
                id="config-key"
                placeholder="e.g., system.maintenance_mode"
                value={formData.key}
                onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                className={`border-gray-300 ${formErrors.key ? 'border-red-500' : ''}`}
                disabled={!!editingConfig}
              />
              {formErrors.key && (
                <p className="text-sm text-red-600">{formErrors.key}</p>
              )}
              <p className="text-xs text-gray-500">
                Use dots to group related settings (e.g., auth.max_attempts, system.timeout)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="config-value" className="text-black">Configuration Value</Label>
              <Textarea
                id="config-value"
                placeholder="Enter the configuration value..."
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                className={`border-gray-300 min-h-[100px] ${formErrors.value ? 'border-red-500' : ''}`}
              />
              {formErrors.value && (
                <p className="text-sm text-red-600">{formErrors.value}</p>
              )}
              <p className="text-xs text-gray-500">
                Can be text, numbers, JSON, or any string value
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowConfigForm(false)}
              className="border-gray-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveConfig}
              className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white"
            >
              {editingConfig ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfig} onOpenChange={() => setDeleteConfig(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-black">Delete Configuration</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600">
              Are you sure you want to delete the configuration "{deleteConfig?.key}"? 
              This action cannot be undone and may affect system functionality.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete} 
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}